import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, Sparkles, Trophy, Trash2, Clock, CheckCircle2, EyeOff } from "lucide-react";

export default function ClientWins() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("win.meta_title"));
  const [suggestions, setSuggestions] = useState(null);
  const [wins, setWins] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/story-highlights/suggestions"); setSuggestions(Array.isArray(data) ? data : []); } catch { setSuggestions([]); }
    try { const { data } = await api.get("/coach/story-highlights"); setWins(Array.isArray(data) ? data : []); } catch { setWins([]); }
  }, []);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const createFrom = async (s) => {
    setBusy(s.client_id + s.suggested_title);
    try {
      await api.post("/coach/story-highlights/from-suggestion", {
        client_id: s.client_id,
        suggested_title: s.suggested_title,
        suggested_before: s.suggested_before,
        suggested_after: s.suggested_after,
        suggested_quote: s.suggested_quote,
        suggested_reflection: s.suggested_reflection,
        suggested_tags: s.suggested_tags || [],
        request_client_approval: true,
      });
      toast.success(t("win.requested"));
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  const remove = async (w) => {
    if (!window.confirm(t("win.delete_confirm"))) return;
    try { await api.delete(`/coach/story-highlights/${w.id}`); toast.success(t("win.deleted")); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="coach-client-wins-page">
      <Seo title={t("win.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="win-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.back_dashboard")}
      </Link>
      <PageHeader eyebrow={t("win.eyebrow")} title={t("win.title")} subtitle={t("win.subtitle")} />

      {/* Suggestions */}
      <p className="mb-2 flex items-center gap-1.5 font-display text-lg text-foreground"><Sparkles className="h-4 w-4 text-primary" /> {t("win.suggestions")}</p>
      {suggestions === null ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
      ) : suggestions.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="win-sugg-empty">{t("win.suggestions_empty")}</p>
      ) : (
        <div className="space-y-2">
          {suggestions.map((s, i) => (
            <div key={i} data-testid={`win-sugg-${i}`} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-foreground">{s.suggested_title}</p>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">{Math.round((s.signal_strength || 0) * 100)}%</span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{s.client_name}</p>
              {s.suggested_quote && <p className="mt-2 border-l-2 border-primary/40 pl-2 text-sm italic text-foreground">“{s.suggested_quote}”</p>}
              <div className="mt-3 flex justify-end">
                <Button size="sm" className="gap-1.5 rounded-full" disabled={busy === s.client_id + s.suggested_title} onClick={() => createFrom(s)} data-testid={`win-create-${i}`}>
                  <Trophy className="h-3.5 w-3.5" /> {t("win.create")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* My wins */}
      <p className="mb-2 mt-6 flex items-center gap-1.5 font-display text-lg text-foreground"><Trophy className="h-4 w-4 text-primary" /> {t("win.my_wins")}</p>
      {wins === null ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : wins.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="win-empty">{t("win.empty")}</p>
      ) : (
        <div className="space-y-2">
          {wins.map((w) => (
            <div key={w.id} data-testid={`win-item-${w.id}`} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
              {w.cover_image_url ? <img src={fileUrl(w.cover_image_url)} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" /> : null}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate font-semibold text-foreground">{w.title}</p>
                  {w.status === "pending_approval" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700"><Clock className="h-3 w-3" /> {t("win.pending")}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> {t("win.approved")}</span>
                  )}
                  {w.is_private && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground"><EyeOff className="h-3 w-3" /> {t("win.private")}</span>}
                </div>
                {w.quote && <p className="mt-1 line-clamp-2 text-xs italic text-muted-foreground">“{w.quote}”</p>}
              </div>
              <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0 rounded-full text-rose-600" onClick={() => remove(w)} data-testid={`win-del-${w.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
